"use client";

import { useEffect, useRef, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeHighlight from "rehype-highlight";
import rehypeKatex from "rehype-katex";
import rehypeSlug from "rehype-slug";
import rehypeAutolinkHeadings from "rehype-autolink-headings";
import "katex/dist/katex.min.css";
import type { Components } from "react-markdown";

function MermaidBlock({ chart }: { chart: string }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const idRef = useRef(`mermaid-${Math.random().toString(36).slice(2, 9)}`);

  const renderChart = useCallback(async () => {
    if (!containerRef.current) return;
    try {
      const mermaid = (await import("mermaid")).default;
      mermaid.initialize({ startOnLoad: false, theme: "default", securityLevel: "loose" });
      const { svg } = await mermaid.render(idRef.current, chart);
      if (containerRef.current) containerRef.current.innerHTML = svg;
    } catch {
      if (containerRef.current) {
        containerRef.current.innerHTML = `<pre style="color:#F87171;font-size:0.8rem;">Invalid mermaid syntax</pre>`;
      }
    }
  }, [chart]);

  useEffect(() => { renderChart(); }, [renderChart]);

  return (
    <div ref={containerRef} style={{ margin: "1.5rem 0", display: "flex", justifyContent: "center", overflow: "auto", fontSize: "16px", lineHeight: "normal" }} />
  );
}

interface PublishedContentProps {
  content: string;
}

export default function PublishedContent({ content }: PublishedContentProps) {
  const components: Components = {
    code({ className, children, ...props }) {
      const match = /language-(\w+)/.exec(className || "");
      if (match?.[1] === "mermaid") {
        return <MermaidBlock chart={String(children).trim()} />;
      }
      return <code className={className} {...props}>{children}</code>;
    },
    pre({ children, ...props }) {
      const child = children as React.ReactElement;
      if (child?.type === MermaidBlock) return <>{children}</>;
      return <pre {...props}>{children}</pre>;
    },
  };

  return (
    <>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[rehypeKatex, rehypeHighlight, rehypeSlug, [rehypeAutolinkHeadings, { behavior: "wrap" }]]}
        components={components}
      >
        {content}
      </ReactMarkdown>
    </>
  );
}
